import { CircleHelp } from 'lucide-react'
import { cn } from '../../../../lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../tailwindComponents/ui/tooltip'

export type KpiTooltipKey =
  | 'sales'
  | 'inventoryAtRetail'
  | 'grossMargin'
  | 'markdowns'
  | 'inflow'
  | 'annualTurn'
  | 'openToBuy'
  | 'totalTransactions'
  | 'averageOrderValue'
  | 'unitsPerTransaction'
  | 'totalUnitsSold'
  | 'retailOrbitHealthScore'
  | 'cashMargin'
  | 'cashRevenue'
  | 'cashPurchases'
  | 'cashMarginRow'
  | 'cashExpenses'
  | 'netCashFlow'

export const KPI_TOOLTIP_CONTENT: Record<KpiTooltipKey, { label: string; content: string }> = {
  sales: {
    label: 'Sales',
    content: 'Your net sale amount reflects the total value of merchandise sold to your customers. Markdowns and sales tax (or VAT) are subtracted',
  },
  inventoryAtRetail: {
    label: 'Inventory at Retail',
    content: 'The total retail value of your current on-hand inventory. This is what your inventory would sell for at full price today.',
  },
  grossMargin: {
    label: 'Gross Margin',
    content: 'Gross Margin is the difference between net sales and the cost of goods sold (COGS). It is expressed as (sales − COGS) / sales over a given period.',
  },
  markdowns: {
    label: 'Markdowns',
    content: "Markdowns are price cuts you take on merchandise that isn't selling at full price — to clear it out, keep it moving, or match what competitors are charging. They reduce your net sales, since you're bringing in less revenue per item than planned.",
  },
  inflow: {
    label: 'Inflow',
    content: "Inflow is the value of new inventory arriving into your store or warehouse during a period — everything you've received, whether it was ordered this month or earlier. It's compared against your plan to see if you're bringing in stock faster or slower than budgeted.",
  },
  annualTurn: {
    label: 'Annual Turn',
    content: 'Annual Turn is the number of times that your average stock is sold within the past 12 months. Formula: Annual Turn = Annual Sales / Average Monthly Inventory.',
  },
  openToBuy: {
    label: 'Open to buy',
    content: "Open to buy is how much you have left to spend on new inventory this month. It's your planned budget minus what you've already ordered.",
  },
  totalTransactions: {
    label: 'Total transactions',
    content: 'The number of separate purchases customers made during the period. Each checkout counts as one transaction, no matter how many items were in it.',
  },
  averageOrderValue: {
    label: 'Average order value',
    content: 'The typical dollar amount a customer spends per transaction. Formula: net sales ÷ total transactions.',
  },
  unitsPerTransaction: {
    label: 'Units / transaction',
    content: 'The average number of items a customer buys per checkout. Formula: total units sold ÷ total transactions. Helps you see whether people are buying more per trip or just more often.',
  },
  totalUnitsSold: {
    label: 'Total units sold',
    content: 'The total count of individual items sold during the period, regardless of price or category.',
  },
  retailOrbitHealthScore: {
    label: 'RetailORBIT Health Score™',
    content: "A composite score (0–100) across five key metrics, benchmarked against retailers in your vertical, derived from Management One's 40-year proprietary retail intelligence database. Scores update monthly.",
  },
  cashMargin: {
    label: 'Cash Margin',
    content: 'This card breaks down how each dollar of net sales is distributed across inventory purchases, operating expenses, and remaining cash flow. The margin values are estimated based on historical data and may not reflect actual results.',
  },
  cashRevenue: {
    label: 'Revenue',
    content: 'Net revenue for the trailing 12 months. Edit to model a different revenue scenario.',
  },
  cashPurchases: {
    label: 'Purchases',
    content: 'Cost of goods purchased (inflow cost) as a % of net sales.',
  },
  cashMarginRow: {
    label: 'Cash Margin',
    content: 'The percentage of revenue remaining after inventory purchases.',
  },
  cashExpenses: {
    label: 'Expenses',
    content: 'Estimated operating expense rate as a % of net sales.',
  },
  netCashFlow: {
    label: 'Net Cash Flow',
    content: 'Estimated cash remaining after purchases and expenses.',
  },
}

export function getKpiTooltipText(key: KpiTooltipKey) {
  return KPI_TOOLTIP_CONTENT[key].content
}

export function getKpiTooltipLabel(key: KpiTooltipKey) {
  return KPI_TOOLTIP_CONTENT[key].label
}

export function KpiHelpIcon({
  tooltipKey,
  className,
  iconClassName,
}: {
  tooltipKey: KpiTooltipKey;
  className?: string;
  iconClassName?: string;
}) {
  const { label, content } = KPI_TOOLTIP_CONTENT[tooltipKey] || {
    label: 'Info',
    content: 'No description available.',
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`About ${label}`}
            className={cn('inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[#98A2B3] hover:text-[#667085] focus:outline-none cursor-pointer', className)}
          >
            <CircleHelp className={cn('h-[12px] w-[12px]', iconClassName)} strokeWidth={1.5} />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="center"
          sideOffset={6}
          collisionPadding={16}
          className="z-50 max-w-[280px] rounded-lg border border-[#344054] bg-[#344054] px-3 py-2 text-xs leading-relaxed text-white shadow-lg"
        >
          <p className="font-medium">{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default KpiHelpIcon


